import { call, put, takeEvery } from "redux-saga/effects";
// import * as calendarActions from "./actions";
import * as calendarTypes from "Store/calendar/constants";
import { Toast } from "utils/toast";
import ERRORS from "constants/localization/errors";
import SUCCESSES from "constants/localization/successes";
import * as globalActions from "Store/global/actions";
import * as studentApi from "Store/students/api";
import * as studentTestsApi from "Store/students/studentsTests/api";
import {
  rescheduleClassroomLessonsApi,
  rescheduleClassroomTestsApi,
  rescheduleClassroomWorksheetsApi,
  updateClassroomSessionApi
} from "Store/classrooms/api";
import moment from "moment";
import { handleFetchStudentCalendarDataSaga } from "Store/students/saga";
import { handleFetchClassCalendarDataSaga } from "Store/classrooms/saga";
import { backendDateFormat, getSessionEndTime } from "utils/timeHelpers";
import { rescheduleStudentLessonsApi } from "Store/students/studentsLessons/api";
import { rescheduleStudentWorksheetsApi } from "Store/students/studentsWorksheets/api";

function* handleRescheduleGroupStudentCalendarSaga(
  action: calendarTypes.IHandleRescheduleGroupStudentCalendar
) {
  const {
    payload: { student_id, assignment_date, due_date, data }
  } = action;

  yield put(globalActions.setActionSpinner(true));
  try {
    const errors = {
      sessions: [],
      lessons: [],
      worksheets: [],
      tests: []
    };
    for (const key in data) {
      if (data[key].length > 0) {
        switch (key) {
          case "sessions":
            for (const index in data[key]) {
              const session = data.sessions[index];
              const sessionBody = {
                end: getSessionEndTime(assignment_date, session.duration_seconds),
                start: assignment_date,
                student_session_id: session.id,
                student_id
              };
              const { error: sessionError } = yield studentApi.updateStudentSessionApi(sessionBody);
              if (sessionError) {
                errors.sessions.push(sessionError);
              }
            }

            break;
          case "lessons":
            const lessons = data.lessons.map((lesson) => ({
              assignment_date,
              due_date,
              id: lesson.id,
              student_id
            }));
            const { error } = yield call(rescheduleStudentLessonsApi, {
              student_lessons: lessons
            });
            if (error) {
              errors.lessons.push(error);
            }
            break;
          case "worksheets":
            const worksheets = data.worksheets.map((worksheet) => ({
              assignment_date,
              due_date,
              id: worksheet.id,
              student_id
            }));
            const responseWorksheets = yield rescheduleStudentWorksheetsApi({
              student_worksheets: worksheets
            });
            if (responseWorksheets?.message) {
              errors.worksheets.push(responseWorksheets?.message);
            }
            break;
          case "tests":
            for (const index in data[key]) {
              const test = data.tests[index];
              const timeStep = moment.duration(moment(test.due_date).diff(test.assignment_date));
              const dueDate = moment(assignment_date)
                .add(timeStep.asMilliseconds(), "milliseconds")
                .format(backendDateFormat);
              const duePayload = {
                student_test_id: test.id,
                due_date: due_date || dueDate,
                student_id
              };
              const assignmentPayload = {
                student_test_id: test.id,
                assignment_date,
                student_id
              };
              const responseDueDate = yield call(
                studentTestsApi.updateStudentTestDueDateApi,
                duePayload
              );
              if (responseDueDate?.message) {
                errors.tests.push(responseDueDate?.message);
              }
              const responseAssignment = yield call(
                studentTestsApi.updateStudentTestAssignmentDateApi,
                assignmentPayload
              );
              if (responseAssignment?.message) {
                errors.tests.push(responseAssignment?.message);
              }
            }

            break;
        }
      }
    }
    yield checkErrors(errors);
    yield call(handleFetchStudentCalendarDataSaga);
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleRescheduleGroupStudentCalendarSaga);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* handleRescheduleGroupClassCalendarSaga(
  action: calendarTypes.IHandleRescheduleGroupClassCalendar
) {
  const {
    payload: { classroom_id, assignment_date, due_date, data }
  } = action;
  yield put(globalActions.setActionSpinner(true));
  try {
    const errors = {
      sessions: [],
      lessons: [],
      worksheets: [],
      tests: []
    };
    for (const key in data) {
      if (data[key].length > 0) {
        switch (key) {
          case "sessions":
            for (const index in data[key]) {
              const session = data.sessions[index];
              const sessionBody = {
                end: getSessionEndTime(assignment_date, session.duration_seconds),
                start: assignment_date,
                classroom_session_id: session.id,
                classroom_id
              };
              const { error } = yield updateClassroomSessionApi(sessionBody);
              if (error) {
                errors.sessions.push(error);
              }
            }

            break;
          case "lessons":
            const lessons = data.lessons.map((lesson) => ({
              assignment_date,
              due_date,
              classroom_lesson_id: lesson.id
            }));
            const lessonsPayload = {
              classroom_id,
              lessons
            };
            const { error: lessonsError } = yield rescheduleClassroomLessonsApi(lessonsPayload);
            if (lessonsError) {
              errors.lessons.push(lessonsError);
            }

            break;
          case "worksheets":
            const worksheets = data.worksheets.map((worksheet) => ({
              assignment_date,
              due_date,
              classroom_worksheet_id: worksheet.id
            }));
            const worksheetsPayload = {
              classroom_id,
              worksheets
            };
            const responseWorksheets = yield rescheduleClassroomWorksheetsApi(worksheetsPayload);
            if (responseWorksheets?.message) {
              errors.worksheets.push(responseWorksheets?.message);
            }

            break;
          case "tests":
            const tests = data.tests.map((test) => {
              const timeStep = moment.duration(moment(test.due_date).diff(test.assignment_date));
              const dueDate = moment(assignment_date)
                .add(timeStep.asMilliseconds(), "milliseconds")
                .format(backendDateFormat);
              return {
                assignment_date,
                due_date: due_date || dueDate,
                classroom_test_id: test.id
              };
            });
            const testsPayload = {
              classroom_id,
              tests
            };

            const { error: testsError } = yield rescheduleClassroomTestsApi(testsPayload);
            if (testsError) {
              errors.tests.push(testsError);
            }
            break;
        }
      }
    }

    yield checkErrors(errors);
    yield call(handleFetchClassCalendarDataSaga, {
      payload: classroom_id
    });
  } catch (error) {
    yield put(globalActions.setErrorSignal(true));
    Toast.error(ERRORS.handleRescheduleGroupStudentCalendarSaga);
  } finally {
    yield put(globalActions.setActionSpinner(false));
  }
}

function* checkErrors(errors) {
  if (
    errors.tests.length > 0 ||
    errors.sessions.length > 0 ||
    errors.lessons.length > 0 ||
    errors.worksheets.length > 0
  ) {
    Object.keys(errors).forEach((key) => {
      if (errors[key].length > 0) {
        Toast.error(`Something went wrong rescheduling ${key}.`, errors[key].join(", "));
      } else {
        Toast.success(`${key} rescheduled successfully.`);
      }
    });
  } else {
    Toast.success(SUCCESSES.handleRescheduleGroupStudentCalendarSaga);
  }
}

export default function* calendarSaga() {
  yield takeEvery(
    calendarTypes.HANDLE_RESCHEDULE_GROUP_STUDENT_CALENDAR,
    handleRescheduleGroupStudentCalendarSaga
  );
  yield takeEvery(
    calendarTypes.HANDLE_RESCHEDULE_GROUP_CLASS_CALENDAR,
    handleRescheduleGroupClassCalendarSaga
  );
}
